// Per-admin unread-thread counter + a one-call shared Realtime
// subscription that broadcasts thread / message changes into the
// TanStack cache. Drop it in once at the top of AdminLayout so every
// admin page benefits (badge updates everywhere, no duplicate sockets).
//
// "Unread" definition: thread with status != 'resolved' AND
// (last_message_at > admin's last_read_at OR no read row exists).
// We exclude resolved threads so closed historical convos don't keep
// inflating the badge.

import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useSession } from "./useSession";

interface ThreadLite { id: string; status: string; last_message_at: string }
interface ReadLite   { thread_id: string; last_read_at: string }

export function useAdminThreadUnread(): { count: number; loading: boolean } {
  const { session } = useSession();
  const adminId = session?.user.id ?? null;

  const q = useQuery({
    queryKey: ["admin-thread-unread", adminId],
    enabled: !!adminId,
    queryFn: async () => {
      if (!adminId) return 0;
      const [threads, reads] = await Promise.all([
        supabase
          .from("patient_message_threads")
          .select("id, status, last_message_at")
          .neq("status", "resolved")
          .limit(1000),
        supabase
          .from("admin_thread_reads")
          .select("thread_id, last_read_at")
          .eq("admin_id", adminId)
          .limit(1000),
      ]);
      if (threads.error) throw threads.error;
      if (reads.error) throw reads.error;
      const readMap = new Map<string, string>();
      for (const r of (reads.data as ReadLite[] | null) ?? []) {
        readMap.set(r.thread_id, r.last_read_at);
      }
      let n = 0;
      for (const t of (threads.data as ThreadLite[] | null) ?? []) {
        const last = readMap.get(t.id);
        if (!last || new Date(t.last_message_at).getTime() > new Date(last).getTime()) n++;
      }
      return n;
    },
    refetchInterval: 60_000, // realtime is primary; this is a safety net
  });

  return { count: q.data ?? 0, loading: q.isLoading };
}

// Single shared subscription. Call once from AdminLayout. Every push
// triggers a TanStack invalidate on the relevant keys; the page-level
// hooks (useThreads / useThreadMessages / useThreadNotes / unread)
// then refetch automatically.
export function useAdminRealtimeBus(): void {
  const qc = useQueryClient();
  const { session } = useSession();
  const adminId = session?.user.id;

  useEffect(() => {
    if (!adminId) return;
    const ch = supabase
      .channel("admin-messages-bus")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "patient_message_threads" },
        () => {
          qc.invalidateQueries({ queryKey: ["admin-message-threads"] });
          qc.invalidateQueries({ queryKey: ["admin-thread-unread"] });
        },
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "patient_messages" },
        (payload) => {
          const newRow = payload.new as { patient_id?: string } | null;
          qc.invalidateQueries({ queryKey: ["admin-message-threads"] });
          qc.invalidateQueries({ queryKey: ["admin-thread-unread"] });
          if (newRow?.patient_id) {
            qc.invalidateQueries({
              queryKey: ["admin-thread-messages", newRow.patient_id],
            });
          }
        },
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "thread_internal_notes" },
        (payload) => {
          const newRow = payload.new as { patient_id?: string } | null;
          if (newRow?.patient_id) {
            qc.invalidateQueries({
              queryKey: ["admin-thread-notes", newRow.patient_id],
            });
          }
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(ch);
    };
  }, [adminId, qc]);
}
